import { createRef, Component, lazy, Suspense } from "react";
import { bool, func, string } from "prop-types";
import styled from "styled-components/macro";
import isEqual from "lodash/isEqual";

import { routes } from "../../constants";

import Meta from "../meta";

class Alerts extends Component {
  constructor(props) {
    super(props);

    this.getAlertsDetails = this.getAlertsDetails.bind(this);
    this.getAlertsComponent = this.getAlertsComponent.bind(this);
    this.setTitle = this.setTitle.bind(this);

    this._isMounted = false;
    this.AlertsComponents = {};
    this.container = createRef();
    this.defaultTitle = "My Alerts";

    this.state = {
      alertDetails: null,
      title: this.defaultTitle,
    };
  }

  /**
   * Get alert listings for specified ID or
   * default to first saved alert, if available
   * @public
   */
  getAlertsDetails() {
    const { alerts, alertsDetails, fetchAlertsDetails, history, match } =
      this.props;
    const paramId = !!match && !!match.params && parseInt(match.params.id, 10);
    let nextId = isNaN(paramId) || (!paramId && paramId !== 0) ? null : paramId;

    if (nextId === null && !!alerts && !!alerts.length) {
      const firstWithUpdates = alerts.find(alert => !!alert.updatedCount);

      if (!!firstWithUpdates) {
        nextId = firstWithUpdates.id;
      } else {
        nextId = alerts[0].id;
      }
    }

    if (nextId !== paramId) {
      const nextPath =
        nextId === null
          ? routes.alerts.path
          : `${routes.alerts.path}/${nextId}`;
      history.replace(nextPath);
    }

    let found = false;

    if (nextId !== null) {
      if (!!alertsDetails && !!alertsDetails.length) {
        found = alertsDetails.find(
          detail => !!detail.savedAlert && detail.savedAlert.id === nextId
        );
      }

      if (!found) {
        fetchAlertsDetails(nextId);
      } else {
        this.setTitle(found.savedAlert.name);
      }
    }

    this.setState({ alertDetails: found || null });
  }

  /**
   * Get the correct alerts component for the target device
   * * Uses code React.lazy for code splitting
   * @public
   */
  getAlertsComponent() {
    const { AlertsComponents } = this;
    const {
      alertsError,
      alertsLoading,
      alertsDetailsError,
      alertsDetailsLoading,
      clearAlertsDetailsError,
      fetchAlerts,
      fetchAlertsDetails,
      targetDevice,
      ...rest
    } = this.props;

    if (!!targetDevice) {
      if (!AlertsComponents[targetDevice]) {
        AlertsComponents[targetDevice] = lazy(
          () => import(`./components/Alerts.${targetDevice}`)
        );
      }

      const AlertsComponent = AlertsComponents[targetDevice];
      const alertsProps = {
        ...rest,
        ...this.state,
        error: alertsError || alertsDetailsError,
        loading: alertsLoading || alertsDetailsLoading,
        targetDevice: targetDevice,
      };

      return <AlertsComponent {...alertsProps} />;
    } else {
      return null;
    }
  }

  /**
   * Update page title with selected alert name,
   * if available
   * @param {string} name
   */
  setTitle(name) {
    if (this._isMounted) {
      const nextTitle = !!name ? `“${name}” Alert Updates` : this.defaultTitle;
      this.setState({ title: nextTitle });
    }
  }

  componentDidMount() {
    this._isMounted = true;

    const { alerts, fetchAlerts } = this.props;

    if (!alerts || !alerts.length) {
      fetchAlerts();
    } else {
      this.getAlertsDetails();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !isEqual(prevProps.match, this.props.match) ||
      !isEqual(prevProps.alerts, this.props.alerts) ||
      !isEqual(prevProps.alertsDetails, this.props.alertsDetails)
    ) {
      this.getAlertsDetails();
    }

    if (
      prevProps.alertsLoading &&
      !this.props.alertsLoading &&
      (!this.props.alerts || !this.props.alerts.length) &&
      !!this.props.match &&
      !!this.props.match.params &&
      !!this.props.match.params.id
    ) {
      this.props.history.replace(routes.alerts.path);
    }

    if (
      !!this.props.alertsDetailsError &&
      (!this.props.alerts ||
        !this.props.alerts.length ||
        this.props.alertsDetailsError === "Saved Alert not found")
    ) {
      this.props.clearAlertsDetailsError();
      this.props.history.replace(routes.alerts.path);
    }
  }

  render() {
    const { container, getAlertsComponent } = this;
    const { title } = this.state;

    return (
      <Container ref={container}>
        <Meta title={title} />
        <Suspense fallback={null}>{getAlertsComponent()}</Suspense>
      </Container>
    );
  }
}

const Container = styled.div`
  height: 100%;
`;

Alerts.propTypes = {
  alertsError: string,
  alertsLoading: bool,
  alertsDetailsError: string,
  alertsDetailsLoading: bool,
  analyticsLabel: string,
  clearAlertsDetailsError: func.isRequired,
  fetchAlerts: func.isRequired,
  fetchAlertsDetails: func.isRequired,
  targetDevice: string,
};

Alerts.defaultProps = {
  analyticsLabel: "alerts",
};

export default Alerts;
